import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import { Button, IconButton, Tooltip } from '@mui/material';
import {
  DataGrid,
  DateField,
  FunctionField,
  PagedList,
  TextField,
} from '../../components';
import { ListToolbar } from '../../components/list/ListToolbar';
import { ArrowBackIcon, InfoIcon } from '../../assets';
import { InventoryTrackingForm } from './InventoryTrackingForm';
import type { InventoryAdjustmentSummary } from './InventoryTrackingForm';
import { getDifference } from '../../lib';
// import {  useDispatch } from '../../state';
import { makeStyles, TextButton } from '../../themes';
import {
  WarehouseLocationPhysicalItemAPI as _WarehouseLocationPhysicalItemAPI,
  BulkComponentProps,
  AddPhysicalItemsToWarehouseLocationRequest,
} from '../../types';
import {
  EditInventoryItemProps,
  CreateInventoryPhysicalItem,
  EditInventoryTrackingMode as Mode,
} from './utils';

interface WarehouseLocationPhysicalItemAPI
  extends _WarehouseLocationPhysicalItemAPI {
  id: number;
}

export function getInventoryAdjustmentSummary(
  originalQuantity: number,
  updatedQuantity?: number | string,
): InventoryAdjustmentSummary | undefined {
  if (updatedQuantity === undefined || updatedQuantity === '') {
    return undefined;
  }

  const parsedUpdatedQuantity = Number(updatedQuantity);
  if (
    !Number.isFinite(parsedUpdatedQuantity) ||
    parsedUpdatedQuantity <= originalQuantity
  ) {
    return undefined;
  }

  return {
    originalQuantity,
    updatedQuantity: parsedUpdatedQuantity,
    increaseQuantity: parsedUpdatedQuantity - originalQuantity,
  };
}

export function getPhysicalItemAvailableQuantityForOrder(
  physicalItem: Pick<
    WarehouseLocationPhysicalItemAPI,
    'totalAvailable' | 'allocatedOrderQuantities'
  >,
  orderLineItemId?: number,
  allocationCredit?: number,
): number {
  const previouslyAllocated =
    allocationCredit ??
    (orderLineItemId
      ? physicalItem.allocatedOrderQuantities?.find(
          (allocation) => allocation.orderLineItemID === orderLineItemId,
        )?.quantity || 0
      : 0);

  return (physicalItem.totalAvailable ?? 0) + previouslyAllocated;
}

interface ApiResponse {
  itemSkuQuantity: number;
  itemSkuPhysicalItemQty: number;
  numberOfRows: number;
  results: Array<WarehouseLocationPhysicalItemAPI>;
}

interface Props {
  receiptId?: string | number;
  warehouseLocationId: string | number;
  moveableUnitId?: string | number | null;
  customerItemId: number;
  sku: string;
  canEdit?: boolean;
  getPhysicalItemsUrlOverride?: string;
  mode?: Mode;
  orderLineItemId?: number;
  /** Pending reservations only when editing additional stock after a completed pick. */
  allocationCredits?: Record<number, number>;
  handleBack?: () => void;
  handleSubmitOverride?: (
    payload: AddPhysicalItemsToWarehouseLocationRequest,
  ) => void;
  setMuViewVersion?: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Optional updated SKU quantity from parent form (like EditInventoryItemForm)
   */
  updatedSkuQuantity?: number | string;
  /**
   * Original on-hand quantity for the inventory row or shared internal-MU pool
   * being edited. Used to describe and cap tracking for a positive adjustment.
   */
  originalSkuQuantity?: number;
  /**
   * Automatically open the add inventory tracking modal on mount
   */
  autoOpen?: boolean;
}

interface ItemStats {
  itemSkuQuantity: number;
  itemSkuPhysicalItemQty: number;
}

interface BulkProps
  extends BulkComponentProps<WarehouseLocationPhysicalItemAPI> {
  mode?: Mode;
  receiptId?: string | number;
  warehouseLocationId: string | number;
  moveableUnitId?: string | number | null;
  customerItemId: number;
  sku: string;
  itemStats: ItemStats;
  selectedIdsOverride: Array<string | number>;
  selectedRowsOverride: Record<string, WarehouseLocationPhysicalItemAPI>;
  handleClearSelected: () => void;
  handleSubmit: (values: EditInventoryItemProps) => Promise<void>;
  formikProps: FormikProps<EditInventoryItemProps>;
  /**
   * Optional updated SKU quantity from parent form (like EditInventoryItemForm)
   */
  updatedSkuQuantity?: number | string;
  orderLineItemId?: number;
  allocationCredits?: Record<number, number>;
}

export function InventoryPhysicalItems({
  receiptId,
  warehouseLocationId,
  moveableUnitId,
  customerItemId,
  sku,
  canEdit,
  getPhysicalItemsUrlOverride,
  mode = 'edit',
  orderLineItemId,
  allocationCredits,
  handleBack,
  handleSubmitOverride,
  setMuViewVersion,
  updatedSkuQuantity,
  originalSkuQuantity,
  autoOpen = false,
}: Props) {
  const { classes, css } = useStyles();
  // const dispatch = useDispatch();

  // const [viewVersion, setViewVersion] = useState(0);
  const [rows, setRows] = useState<
    Record<number, WarehouseLocationPhysicalItemAPI>
  >({});
  const [ids, setIds] = useState<Array<number>>([]);
  const [bulkSelectedRecordsIds, setBulkSelectedRecordIds] = useState<
    Array<string | number>
  >([]);
  const [bulkSelectedRecordsRows, setSelectedBulkRecordsRows] = useState<
    Record<string, WarehouseLocationPhysicalItemAPI>
  >({});
  const [itemStats, setItemStats] = useState<ItemStats>({
    itemSkuQuantity: 0,
    itemSkuPhysicalItemQty: 0,
  });
  const [item, setItem] = useState<EditInventoryItemProps>({
    receiptID: receiptId,
    sku,
    customerItemID: customerItemId,
    skuQuantity: itemStats.itemSkuQuantity,
    createPhysicalItems: {},
    primaryQuantityRemaining: 0,
  });
  const [showAddModal, setShowAddModal] = useState(false);

  const inventoryAdjustment = getInventoryAdjustmentSummary(
    originalSkuQuantity ?? itemStats.itemSkuQuantity,
    updatedSkuQuantity,
  );

  // const incrementViewVersion = useCallback(() => {
  //   setViewVersion((prev) => ++prev);
  //   setMuViewVersion?.((prev) => ++prev);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const handleClearSelected = useCallback(() => {
    setBulkSelectedRecordIds([]);
    setSelectedBulkRecordsRows({});
  }, []);

  const handleGetResponseData = useCallback((value: ApiResponse) => {
    if (value) {
      setItemStats({
        itemSkuQuantity: value.itemSkuQuantity,
        itemSkuPhysicalItemQty: value.itemSkuPhysicalItemQty,
      });
      setItem((cur) => ({ ...cur, skuQuantity: value.itemSkuQuantity }));
    }
  }, []);

  const getBulkSelectedRecords = useCallback(
    (
      ids: Array<string | number>,
      rows: Record<string, WarehouseLocationPhysicalItemAPI>,
    ) => {
      setBulkSelectedRecordIds(ids);
      setSelectedBulkRecordsRows(rows);
    },
    [],
  );

  const handleSubmit = async (values: EditInventoryItemProps) => {
    if (!values) return;
    // Edited physical items
    const arrayValue = Object.values(values.createPhysicalItems);
    // Original physical items
    const itemsArray = Object.values(bulkSelectedRecordsRows ?? {});

    const payload: AddPhysicalItemsToWarehouseLocationRequest = {
      warehouseLocationID: warehouseLocationId,
      physicalItems: arrayValue
        .filter((i) => i.quantity > 0)
        .map((i) => ({
          id: i.moveableUnitPhysicalItemId,
          physicalItemID: i.id ? (i.id as number) : undefined,
          moveableUnitID: moveableUnitId,
          receiptID: i.receiptId,
          quantity: i.quantity,
          physicalItemAPI: {
            id: i.id as number,
            customerItemID: customerItemId ? customerItemId : undefined,
            sku,
            serialNumber: i.serialNumber,
            lotNumber: i.lotNumber,
            expirationDate: i.expirationDate,
          },
        })),
    };
    if (mode === 'edit') {
      const removedPhysicalItemIds = getDifference<number>(
        itemsArray.map((i) => i.id).filter((i) => i !== undefined),
        arrayValue
          .map((i) => i.moveableUnitPhysicalItemId)
          .filter((i) => i !== undefined),
      );
      payload.physicalItemsToDelete = itemsArray
        .filter((i) => removedPhysicalItemIds.includes(i.id))
        .map((i) => ({
          moveableUnitPhysicalItemID: i.id!,
          receiptID: i.receiptID as number,
        }));
    }
    if (handleSubmitOverride) {
      handleSubmitOverride(payload);
      handleClearSelected();
      // clear the form
      formik.setFieldValue('createPhysicalItems', {});
      return;
    }
    // const { error } = await dispatch(
    //   upsertMoveableUnitPhysicalItems({ receiptId, moveableUnitId, payload }),
    // );
    // if (!error) {
    //   incrementViewVersion();
    //   handleClearSelected();
    //   // clear the form
    //   formik.setFieldValue('createPhysicalItems', {});
    // }
  };

  const formik = useFormik<EditInventoryItemProps>({
    initialValues: item,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const handleShowAdd = () => {
    setShowAddModal(true);
  };

  const handleHideAdd = useCallback(async (doSubmit?: boolean) => {
    if (doSubmit) {
      await formik.submitForm();
    }
    setShowAddModal(false);
    formik.setFieldValue('createPhysicalItems', {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the add modal when autoOpen is true
  useEffect(() => {
    if (autoOpen) {
      setShowAddModal(true);
    }
  }, [autoOpen]);

  const renderAllocations = useCallback(
    (record: WarehouseLocationPhysicalItemAPI) => {
      const { totalAvailable, allocatedOrderQuantities } = record;
      if (!allocatedOrderQuantities?.length || !totalAvailable) {
        return totalAvailable ?? '--';
      }
      if (orderLineItemId) {
        const itemAllocatedTotal = allocationCredits
          ? (allocationCredits[record.id] ?? 0)
          : allocatedOrderQuantities.find(
              (i) => i.orderLineItemID === orderLineItemId,
            )?.quantity;
        if (!itemAllocatedTotal) {
          return totalAvailable;
        }
        return `${totalAvailable} (${itemAllocatedTotal} allocated)`;
      } else {
        return (
          <div>
            <span>{totalAvailable}</span>
            <Tooltip
              slotProps={{ popper: { disablePortal: true } }}
              classes={{ tooltip: classes.tooltip }}
              title={
                <Fragment>
                  <span className={classes.tooltipTitle}>
                    Order allocations
                  </span>
                  {allocatedOrderQuantities?.map((i) => (
                    <span key={i.orderLineItemID}>
                      {`Order #${i.orderID} (Line item #${i.orderLineItemID}) - ${i.quantity}`}
                    </span>
                  ))}
                </Fragment>
              }
            >
              <InfoIcon
                style={{
                  fontSize: 15,
                  marginRight: 4,
                  color: '#FF4F5B',
                }}
              />
            </Tooltip>
          </div>
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderLineItemId, allocationCredits],
  );

  return (
    <div>
      {handleBack && (
        <>
          <div className={classes.navigator}>
            <IconButton
              onClick={handleBack}
              className={classes.backIcon}
              size='small'
            >
              <ArrowBackIcon />
            </IconButton>
            <div className={classes.title}>Inventory tracking</div>
          </div>
          <div className={classes.subtitle}>
            <span>{`MU ${moveableUnitId || '--'}`}</span>
            <span className={classes.spacer}>|</span>
            <span>{`Item ${sku}`}</span>
          </div>
        </>
      )}
      <div className={css({ position: 'relative' })}>
        {canEdit && (
          <ListToolbar
            numSelected={bulkSelectedRecordsIds.length}
            ids={ids}
            rows={rows}
            selectedIds={bulkSelectedRecordsIds}
            selectedRows={bulkSelectedRecordsRows}
            bulkActionButtons={
              <BulkActions
                mode={mode}
                receiptId={receiptId}
                warehouseLocationId={warehouseLocationId}
                moveableUnitId={moveableUnitId}
                customerItemId={customerItemId}
                sku={sku}
                itemStats={itemStats}
                selectedIdsOverride={bulkSelectedRecordsIds}
                selectedRowsOverride={bulkSelectedRecordsRows}
                handleClearSelected={handleClearSelected}
                formikProps={formik}
                handleSubmit={handleSubmit}
                updatedSkuQuantity={updatedSkuQuantity}
                orderLineItemId={orderLineItemId}
                allocationCredits={allocationCredits}
              />
            }
          />
        )}
        <PagedList
          url={
            getPhysicalItemsUrlOverride ||
            `inventory/stock/locations/${warehouseLocationId}/physical-items`
          }
          filters={{
            customerItemID: customerItemId,
            // Manual transfers consume a single receipt. Edit flows can operate
            // on a shared inventory pool and keep their existing scope.
            receiptID: mode === 'transfer' ? receiptId : undefined,
            sku,
            moveableUnitID: moveableUnitId,
            getItemStats: true,
          }}
          setIds={setIds}
          setRows={setRows}
          getResponseData={handleGetResponseData}
          // viewVersion={viewVersion}
          // table-layout='fixed'
          // white-space='nowrap'
          // className={classes.table}
          // getPagingInfo={handleSetPagingInfo}
          // viewVersion={viewVersion}
        >
          <DataGrid<WarehouseLocationPhysicalItemAPI>
            ids={ids}
            rows={rows}
            // hideHeader
            hasBulkActions={canEdit}
            getSelectedRowsAndIds={getBulkSelectedRecords}
            selectedIds={bulkSelectedRecordsIds}
          >
            <TextField
              source='physicalItemAPI.serialNumber'
              label='Serial #'
              headerClassName={classes.noWrap}
              sortable={false}
            />
            <DateField
              source='physicalItemAPI.expirationDate'
              label='Exp date'
              headerClassName={classes.noWrap}
              sortable={false}
            />
            <TextField
              source='physicalItemAPI.lotNumber'
              label='Lot #'
              headerClassName={classes.noWrap}
              sortable={false}
            />
            <TextField
              source='quantity'
              label='On hand'
              headerClassName={classes.noWrap}
              sortable={false}
            />
            <FunctionField<WarehouseLocationPhysicalItemAPI>
              source='totalAvailable'
              label='Available'
              headerClassName={classes.noWrap}
              sortable={false}
              render={renderAllocations}
            />
            <TextField
              source='receiptID'
              label='Receipt #'
              headerClassName={classes.noWrap}
              sortable={false}
            />
            <TextField
              source='age'
              label='Age'
              headerClassName={classes.noWrap}
              sortable={false}
            />
          </DataGrid>
        </PagedList>
        {canEdit && mode === 'edit' && (
          <div className={css({ marginTop: 8 })}>
            <TextButton
              onClick={handleShowAdd}
              disabled={!!bulkSelectedRecordsIds.length}
            >
              Add physical items
            </TextButton>
          </div>
        )}
      </div>
      {showAddModal && (
        <InventoryTrackingForm
          open={showAddModal}
          handleClose={handleHideAdd}
          item={formik.values}
          formikProps={formik}
          mode={mode}
          packageKey=''
          itemKey=''
          remainderSkuPhysicalItemsQuantity={itemStats.itemSkuPhysicalItemQty}
          updatedSkuQuantity={updatedSkuQuantity}
          inventoryAdjustment={inventoryAdjustment}
          nestedKey={['createPhysicalItems']}
        />
      )}
    </div>
  );
}

function BulkActions({
  selectedIdsOverride: selectedIds,
  selectedRowsOverride: selectedRows,
  itemStats,
  formikProps,
  updatedSkuQuantity,
  mode,
  orderLineItemId,
  allocationCredits,
}: BulkProps) {
  const selectedPhysicalItemsTotal = useRef(0);

  const [showEdit, setShowEdit] = useState(false);

  const handleShowEdit = () => {
    let selectedTotal = 0;
    const physicalItems = Object.values(selectedRows).reduce<
      Record<string, CreateInventoryPhysicalItem>
    >((acc, cur) => {
      const previouslyAllocated = allocationCredits
        ? (allocationCredits[cur.id] ?? 0)
        : orderLineItemId
          ? cur.allocatedOrderQuantities?.find(
              (a) => a.orderLineItemID === orderLineItemId,
            )?.quantity || 0
          : 0;
      acc[cur.id + ''] = {
        id: cur.physicalItemID,
        moveableUnitPhysicalItemId: cur.id,
        receiptId: cur.receiptID,
        serialNumber: cur.physicalItemAPI.serialNumber || '',
        lotNumber: cur.physicalItemAPI.lotNumber || '',
        expirationDate: cur.physicalItemAPI.expirationDate,
        quantity:
          mode === 'transfer'
            ? previouslyAllocated
            : (cur.quantity as number) || 0,
        originalQuantity: getPhysicalItemAvailableQuantityForOrder(
          cur,
          orderLineItemId,
          allocationCredits ? previouslyAllocated : undefined,
        ),
      };

      selectedTotal += cur.quantity as number;
      return acc;
    }, {});

    formikProps.setFieldValue('createPhysicalItems', physicalItems);
    selectedPhysicalItemsTotal.current = selectedTotal;

    setShowEdit(true);
  };

  const handleHideEdit = useCallback(async (doSubmit?: boolean) => {
    if (doSubmit) {
      await formikProps.submitForm();
    }
    setShowEdit(false);
    formikProps.setFieldValue('createPhysicalItems', {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Button
        color='primary'
        // style={{ marginLeft: 16 }}
        onClick={handleShowEdit}
        variant='contained'
        disableElevation
      >
        {mode === 'edit' ? 'Edit' : 'Select'}
      </Button>
      {showEdit && (
        <InventoryTrackingForm
          open={showEdit}
          handleClose={handleHideEdit}
          item={formikProps.values}
          formikProps={formikProps}
          mode={mode}
          packageKey=''
          itemKey=''
          remainderSkuPhysicalItemsQuantity={
            itemStats.itemSkuPhysicalItemQty -
            selectedPhysicalItemsTotal.current
          }
          updatedSkuQuantity={updatedSkuQuantity}
          nestedKey={['createPhysicalItems']}
        />
      )}
    </div>
  );
}

const useStyles = makeStyles({
  name: { InventoryItemPhysicalItems: InventoryPhysicalItems },
})((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 24,
    fontWeight: 'bold',
    letterSpacing: 0,
  },
  navigator: {
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
  },
  backIcon: {
    color: 'inherit',
    marginRight: 7,
  },
  subtitle: {
    fontSize: 14,
    letterSpacing: 0,
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {
    marginRight: 4,
    marginLeft: 4,
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  tooltip: {
    backgroundColor: '#FFFFFF',
    padding: '4px 16px',
    borderRadius: 3,
    boxShadow: '0 2px 24px 0 #e5e7f5',
    color: 'var(--basic-grey-500)',
    display: 'flex',
    flexDirection: 'column',
  },
  tooltipTitle: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--basic-black)',
  },
}));
